import React from 'react';
import styled from '@emotion/styled';
import { DSContentBlock } from '@hundred5/design-system';

import { useWorkspace } from '@/features/workspace';

import { useAutomationsFeatureFlag } from '../../hooks';

import AutomationIllustration from './assets/automation-illustration.svg';

interface AutomationsBannerProps {
  type: 'job' | 'test';
}

const AUTOMATIONS_RELEASE_DATE = new Date('2025-04-29T00:00:00Z');

export function AutomationsBanner({ type }: AutomationsBannerProps) {
  const workspace = useWorkspace();
  const isAutomationsFeatureEnabled = useAutomationsFeatureFlag();

  if (!workspace || !isAutomationsFeatureEnabled) return null;

  // don't show test banner for worskpaces created after automations release
  if (type === 'test' && workspace.createdAt > AUTOMATIONS_RELEASE_DATE) {
    return null;
  }

  return (
    <DSContentBlock>
      <DSContentBlock.Title>
        <InnerTitle>
          {type === 'job' ? 'Pipeline automation' : 'Automation has moved'}
          <Tag>New</Tag>
        </InnerTitle>
      </DSContentBlock.Title>
      <DSContentBlock.Content>
        <ContentGrid>
          <Info>
            {type === 'job' ? (
              <>
                Pipeline automation allows you to set up custom automated
                actions at each stage of your recruitment process.
                <br />
                <br />
                You can add automations from the pipeline, at the top of each
                stage. Learn more about using automation here.
                {/* TODO: needs link */}
              </>
            ) : (
              <>
                You can now set up automations from the pipeline, at the top of
                each stage.
                <br />
                <br />
                Any existing automations will still work, and you can learn more
                about using automation here.
                {/* TODO: needs link */}
              </>
            )}
          </Info>
          <Illustration>
            <Image src={AutomationIllustration} />
          </Illustration>
        </ContentGrid>
      </DSContentBlock.Content>
    </DSContentBlock>
  );
}

const InnerTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Tag = styled.span`
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.purple[100]};
  background: ${({ theme }) => theme.colors.orange[20]};
  border-radius: 8px;
  padding: 0px 8px;
  height: 16px;
  display: flex;
  align-items: center;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-areas:
    'info'
    'illustration';
  grid-template-columns: 1fr;
  gap: 24px 80px;

  @media (min-width: 769px) {
    grid-template-areas: 'info illustration';
    grid-template-columns: 1fr 1fr;
  }
`;

const Info = styled.div`
  grid-area: info;
`;

const Illustration = styled.div`
  grid-area: illustration;
  border-radius: 8px;
  background: ${(props) => props.theme.colors.peach[100]};
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const Image = styled.img`
  width: 100%;
  max-height: 160px;
`;
